/**
 * Feedback for directory actions, split the way the house rules split it:
 *
 * - FAILURES get a toast (`toastError`) — the one thing a toast is for here. Success never toasts;
 *   the new row appearing, or the row's own meta line, is the feedback (progress lives in the control).
 * - SUCCESS NOTES ride the row (`flashRow`): a transient replacement for the row's meta line
 *   ("6 images extracted", "styles extracted · tiles are on the canvas"), read back by the item
 *   enrichment layer and gone a few seconds later.
 *
 * Module store on the `stageSignals` pattern: a version counter plus reads, `useSyncExternalStore` on
 * the consuming side, so a bus event never forces anything but the subscribed components.
 */
import { useMemo, useSyncExternalStore } from 'react';
import type { DirectoryItem } from '~/design-system/components/v1_5/Directory';

const listeners = new Set<() => void>();
let version = 0;

function bump(): void {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribeNotify(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function notifyVersion(): number {
  return version;
}

export function notifyServerVersion(): number {
  return 0;
}

/* ── error toasts ─────────────────────────────────────────────────────────────────────────────── */

export interface DeckToast {
  id: number;
  text: string;
}

const TOAST_MS = 4200;
const TOAST_CAP = 3;
let toastSeq = 0;
let toasts: DeckToast[] = [];

/** The current stack, newest last. A stable reference between bumps, as `useSyncExternalStore` needs. */
export function currentToasts(): DeckToast[] {
  return toasts;
}

/** A failure the user has to hear about. Success never comes through here. */
export function toastError(text: string): void {
  const id = ++toastSeq;
  toasts = [...toasts, { id, text }].slice(-TOAST_CAP);
  bump();
  if (typeof window !== 'undefined') {
    window.setTimeout(() => dismissToast(id), TOAST_MS);
  }
}

export function dismissToast(id: number): void {
  if (!toasts.some((t) => t.id === id)) return;
  toasts = toasts.filter((t) => t.id !== id);
  bump();
}

/* ── row work (in-flight) ─────────────────────────────────────────────────────────────────────── */

const works = new Map<string, string>();

/**
 * A job is running against this row. The row wears `updating` and the given meta line ("updating ·
 * extracting images") until `endRowWork` — status rule 2 in `Directory/theme.ts`: the status word
 * takes the meta's first slot and the timestamp drops.
 */
export function beginRowWork(key: string, meta: string): void {
  works.set(key, meta);
  bump();
}

export function endRowWork(key: string): void {
  if (works.delete(key)) bump();
}

/* ── row meta flashes ─────────────────────────────────────────────────────────────────────────── */

const FLASH_MS = 3200;
const flashes = new Map<string, string>();

/** Briefly replace a row's meta line with a result note. `key` is the directory row key (`web:3`). */
export function flashRow(key: string, text: string): void {
  flashes.set(key, text);
  bump();
  if (typeof window !== 'undefined') {
    window.setTimeout(() => {
      if (flashes.get(key) === text) {
        flashes.delete(key);
        bump();
      }
    }, FLASH_MS);
  }
}

/** The active flash for a row, or undefined. Read by the enrichment layer on every version bump. */
export function rowFlash(key: string): string | undefined {
  return flashes.get(key);
}

/**
 * The items with this module's live signals worn on them: an in-flight job turns the row
 * `updating` with its meta line, a result flash replaces the meta for a beat. Subscribed, so both
 * appear and clear without the host doing anything.
 */
export function useRowSignals(items: DirectoryItem[]): DirectoryItem[] {
  useSyncExternalStore(subscribeNotify, notifyVersion, notifyServerVersion);
  return useMemo(() => {
    if (flashes.size === 0 && works.size === 0) return items;
    return items.map((item) => {
      const work = works.get(item.id);
      const flash = flashes.get(item.id);
      if (work) return { ...item, status: 'updating' as const, meta: work };
      return flash ? { ...item, meta: flash } : item;
    });
    // The version counter IS the dependency; the map re-runs when a signal lands or clears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, notifyVersion()]);
}
